import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

interface CollapsibleStatsProps {
  /** The stat cards, the most important first. */
  items: React.ReactNode[];
  /** The grid from `md` up (e.g. `md:grid-cols-4`); a phone stacks them in one column. */
  className?: string;
}

/**
 * A row of stat cards that, on a phone (below 768px), shows only the first one and a chevron that
 * opens the rest underneath, as the old portal's `isMobileExpend` stat boxes.
 */
export const CollapsibleStats = ({ items, className }: Readonly<CollapsibleStatsProps>) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const canCollapse = items.length > 1;

  return (
    <div className="flex flex-col gap-2">
      <div className={cn('grid grid-cols-1 gap-4 max-md:gap-2', className)}>
        {items.map((item, index) => (
          <div key={index} className={cn(index > 0 && !isExpanded && 'max-md:hidden')}>
            {item}
          </div>
        ))}
      </div>
      {canCollapse && (
        <button
          type="button"
          className="flex items-center justify-center self-center rounded-full p-1 text-muted-foreground hover:text-foreground md:hidden"
          aria-expanded={isExpanded}
          aria-label={t(isExpanded ? 'SHOW_LESS' : 'SHOW_MORE')}
          title={t(isExpanded ? 'SHOW_LESS' : 'SHOW_MORE')}
          onClick={() => setIsExpanded((expanded) => !expanded)}
        >
          <ChevronDown className={cn('size-5 transition-transform', isExpanded && 'rotate-180')} />
        </button>
      )}
    </div>
  );
};
